import { isHttpError, sentry } from '@frontend/tracking';

export type LogErrorContext = {
  /** Defaults to `'Custom Context'` if omitted. */
  name?: string;
  data: Record<string, unknown>;
  /**
   * Overrides Sentry's default (stack-trace based) grouping.
   * Useful when several distinct failure modes are reported from the same call site (e.g. a shared
   * react-query `onError` handler) and would otherwise collapse into a single umbrella issue.
   * Not needed for HTTP errors (see `isHttpError`) — those are already fingerprinted by status/method/route.
   */
  fingerprint?: string[];
};

const buildContext = (context?: LogErrorContext) =>
  context ? { name: context.name ?? 'Custom Context', context: context.data } : undefined;

/**
 * Logs an error to the console and to Sentry under the `phone-config` topic.
 *
 * HTTP errors (see `isHttpError`) are routed through `sentry.httpError`, which tags the event as
 * `error.kind: 'http'` and — together with the workspace-wide HTTP error preprocessing integration —
 * automatically derives a readable title and a fingerprint from the request's status/method/route, so
 * distinct HTTP failures don't collapse into one Sentry issue just because they share this call site.
 *
 * @param error - A descriptive message string or an `Error`/`HttpErrorLike` instance (preserves stack trace).
 * @param context - Optional Sentry context payload. `name` defaults to `'Custom Context'`.
 *
 * @example
 * logError('Failed to fetch call routes');
 * logError(err, { name: 'Fetch Call Routes', data: { tenantId } });
 * logError(message, { data: { queryKey }, fingerprint: [message] });
 */
export const logError = (error: string | Error, context?: LogErrorContext) => {
  console.error(error);

  if (isHttpError(error)) {
    sentry.httpError({
      error,
      topic: 'phone-config',
      team: 'fe-phone-config',
      addContext: buildContext(context),
      fingerprint: context?.fingerprint,
    });
    return;
  }

  sentry.error({
    error,
    topic: 'phone-config',
    team: 'fe-phone-config',
    severityLevel: 'error',
    addContext: buildContext(context),
    fingerprint: context?.fingerprint,
  });
};

/**
 * Logs a warning to the console and to Sentry under the `phone-config` topic.
 *
 * @param error - A descriptive message string or an `Error` instance.
 * @param context - Optional Sentry context payload. `name` defaults to `'Custom Context'`.
 *
 * @example
 * logWarning('Device list empty, falling back to default');
 * logWarning(err, { name: 'Device List', data: { tenantId } });
 */
export const logWarning = (error: string | Error, context?: LogErrorContext) => {
  console.warn(error);
  sentry.warn({
    error,
    topic: 'phone-config',
    team: 'fe-phone-config',
    addContext: buildContext(context),
  });
};
